import { useCallback, useEffect, useState } from 'react';
import { AddOnVisibilityOperationType, ModelType, SubscriptionCategory } from '../../common/enums';
import {
  changeSubscriptionFeatures,
  fetchAddOnRules,
  fetchCampaigns,
  fetchCompanyInfo,
  fetchOnlineModelForCode,
  fetchSubscription,
  hasPendingSubscriptionActions,
} from '../../common/fetch';
import { useAddOnVisibility } from '../../common/hooks/useAddOnVisibility';
import { useSelfServiceData } from '../../common/hooks/useSelfServiceData';
import {
  filterChangeOfferAddonsFromRingSubs,
  filterVisibilitiesFromRingSubs,
  isRingSubscription,
} from '../../common/utils/ringUtils';
import { getSubscriptionStatus, getSubscriptionUrlId } from '../../common/utils/subscriptionUtils';
import { SalesType } from '../../generated/api/salesType';
import { SubscriptionType } from '../../generated/api/subscriptionType';
import { useAuth } from '../../public/site/AuthProvider';
import { getOnlineModelFilteringOutNonOfferAddOns } from '../../selfservice/reducers/reducerUtils';
import {
  getSelectedServiceFee,
  prepareOfferAndServiceFeeMap,
} from '../OrderSubscriptionSelection/OrderSubscriptionSelectionUtils';
import { getVoiceSubscriptionCardContents } from '../OrderSubscriptionSelection/content/VoiceSubscriptionCardContent';
import { getAddOnPayload, handleChangeOffer } from '../OrderSubscriptionSelection/subscriptionChangeConfirmationUtils';
import { getAddedAddOns } from '../SubscriptionDetails/addOnDependencyUtils';

import { CampaignModal } from './CampaignModal';
import type { AddedAddon } from '../../common/types/addon';
import type { AddOnRulesResponse } from '../../generated/api/addOnRulesResponse';
import type { CampaignAssociation } from '../../generated/api/campaignAssociation';
import type { CampaignsResponse } from '../../generated/api/campaignsResponse';
import type { CommercialProduct } from '../../generated/api/commercialProduct';
import type { CompanyInfoResponse } from '../../generated/api/companyInfoResponse';
import type { Offer } from '../../generated/api/offer';
import type { OnlineModel } from '../../generated/api/onlineModel';
import type { Subscription } from '../../generated/api/subscription';
import type { SubscriptionChangeFeaturesRequest } from '../../generated/api/subscriptionChangeFeaturesRequest';
import type { SubscriptionHeader } from '../../generated/api/subscriptionHeader';
import type { AssociationRecord } from '../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';

const VOUCHER = 'ETU2025';

const ELIGIBLE_SUBSCRIPTIONS = [
  'Elisa Yritysliittymä 5G Perus (300M)',
  'Elisa Yritysliittymä 5G Perus (600M)',
  'Elisa Yritysliittymä 5G+ Perus (300M)',
];

// Helper function to check if a subscription is eligible for the campaign
const isEligibleSubscription = (subscriptionName?: string): boolean => {
  if (!subscriptionName) {
    return false;
  }
  return ELIGIBLE_SUBSCRIPTIONS.some(eligibleName => subscriptionName.includes(eligibleName));
};

// Component is visible only for users who are part of campaign group
export const SelfServiceCampaign = () => {
  const { authenticatedUser } = useAuth();
  const { contact } = authenticatedUser || {};
  const { subscriptions } = useSelfServiceData();

  const [subscription, setSubscription] = useState<Subscription>();
  const [onlineModel, setOnlineModel] = useState<OnlineModel>();
  const [subscriptionHeader, setSubscriptionHeader] = useState<SubscriptionHeader>();
  const [campaigns, setCampaigns] = useState<CampaignsResponse>();
  const [companyInfo, setCompanyInfo] = useState<CompanyInfoResponse>();

  const [addOnRules, setAddOnRules] = useState<AddOnRulesResponse>();
  const [addOnRulesMobilePbx, setAddOnRulesMobilePbx] = useState<AddOnRulesResponse>();

  const isCampaignActive = useCallback((campaignResponse: CampaignsResponse): boolean => {
    const context = campaignResponse?.campaignContexts?.find(c => c.commercialProductCode);
    if (!context) {
      return false;
    }
    const expirationDate = context.campaignAssociations?.[0]?.expirationDate;
    const effectiveDate = context.campaignAssociations?.[0]?.effectiveDate;

    const now = Date.now();
    const isExpired = expirationDate ? now > expirationDate : false;
    const isNotStarted = effectiveDate ? now < effectiveDate : false;

    return !isExpired && !isNotStarted;
  }, []);

  const fetchCampaignData = useCallback(
    async (subHeader: SubscriptionHeader) => {
      const campaignsQuery = {
        salesType: SalesType.CHANGE_OFFER,
        voucher: VOUCHER,
        onlineModel: ModelType.VoiceSME,
      };

      const campaignsResponse = await fetchCampaigns(campaignsQuery);

      if (!isCampaignActive(campaignsResponse)) {
        return false;
      }

      const [
        onlineModelResponse,
        subscriptionResponse,
        addOnRulesResponse,
        addOnRulesMobilePbxResponse,
        companyInfoResponse,
      ] = await Promise.all([
        fetchOnlineModelForCode(ModelType.VoiceSME),
        fetchSubscription(SubscriptionCategory.VOICE, subHeader.subscriptionId, subHeader.accountMasterId!),
        fetchAddOnRules(),
        fetchAddOnRules(SubscriptionType.MOBILE_PBX),
        fetchCompanyInfo(subHeader.accountMasterId),
      ]);

      setOnlineModel(getOnlineModelFilteringOutNonOfferAddOns(onlineModelResponse));
      setCampaigns(campaignsResponse);
      setSubscription(subscriptionResponse);
      setAddOnRules(addOnRulesResponse);
      setAddOnRulesMobilePbx(addOnRulesMobilePbxResponse);
      setCompanyInfo(companyInfoResponse);

      return true;
    },
    [isCampaignActive]
  );

  const findEligibleSubscription = useCallback(() => {
    if (!subscriptions) {
      return undefined;
    }
    const voiceSubscriptions = subscriptions[SubscriptionCategory.VOICE];
    return voiceSubscriptions?.find(sub => isEligibleSubscription(sub.subscriptionName));
  }, [subscriptions]);

  const validateEligibleSubscription = useCallback(async () => {
    const targetSub = findEligibleSubscription();

    // If no applicable subscription is found, do not proceed
    if (!targetSub) {
      return;
    }

    // Check if change offer is allowed
    const hasPendingActions = await hasPendingSubscriptionActions(
      getSubscriptionUrlId(targetSub),
      targetSub.accountMasterId!
    );
    if (!getSubscriptionStatus(targetSub, hasPendingActions).changeOfferAllowed) {
      return;
    }

    setSubscriptionHeader(targetSub);
  }, [findEligibleSubscription]);

  // Find and validate eligible subscription
  useEffect(() => {
    // Is eligible subscription already found
    if (subscriptionHeader) {
      return;
    }
    if (!subscriptions || !contact?.isPartOfCampaignGroup) {
      return;
    }

    validateEligibleSubscription();
  }, [contact?.isPartOfCampaignGroup, subscriptionHeader, subscriptions, validateEligibleSubscription]);

  // Fetch campaign data when subscription header is set
  useEffect(() => {
    if (!subscriptionHeader || !contact?.isPartOfCampaignGroup) {
      return;
    }

    fetchCampaignData(subscriptionHeader);
  }, [subscriptionHeader, fetchCampaignData, contact?.isPartOfCampaignGroup]);

  const campaignContext = campaigns?.campaignContexts?.find(context => context.commercialProductCode);

  const offerCode = campaignContext?.offerCode;
  const commercialProductCode = campaignContext?.commercialProductCode ?? '';
  const campaignAssociationCode = campaignContext?.campaignAssociations?.[0]?.campaignAssociationCode;
  const campaignExpirationDate = campaignContext?.campaignAssociations?.[0]?.expirationDate;
  const campaignEffectiveDate = campaignContext?.campaignAssociations?.[0]?.effectiveDate;

  const offer = onlineModel?.offers?.find(o => o.offerCode === offerCode);
  const commercialProduct = offer?.commercialProducts?.[0] as CommercialProduct;
  const subscriptionId = subscription?.subscriptionId ?? '';

  const { addOnVisibilities } = useAddOnVisibility(
    commercialProductCode,
    AddOnVisibilityOperationType.CHANGE,
    undefined,
    subscriptionHeader?.accountMasterId
  );

  // Check if campaign has expired or not yet started
  const isCampaignExpired = campaignExpirationDate ? Date.now() > campaignExpirationDate : false;
  const isCampaignNotStarted = campaignEffectiveDate ? Date.now() < campaignEffectiveDate : false;

  if (
    !addOnVisibilities ||
    addOnRules == null ||
    addOnRulesMobilePbx == null ||
    !subscription ||
    !campaigns?.campaignContexts ||
    !subscriptionHeader ||
    !onlineModel ||
    isCampaignExpired ||
    isCampaignNotStarted
  ) {
    return null;
  }

  const buildChangeRequest = (): SubscriptionChangeFeaturesRequest => {
    const isRingSub = isRingSubscription(subscription, companyInfo?.pbxSolutions);
    const handleChangeOfferResult = handleChangeOffer(
      subscription,
      commercialProduct,
      addOnRules,
      addOnRulesMobilePbx,
      isRingSub,
      addOnVisibilities
    );

    const subscriptionProductData = getVoiceSubscriptionCardContents(false, true);
    const selectedSubData = subscriptionProductData.find(subData => subData.selectedOffer === offer?.offerCode);
    const includedAddonCodes: string[] =
      selectedSubData?.services?.filter(s => s.isIncluded && s.guid !== undefined).map(s => s.guid as string) ?? [];
    const addOnAssociations = addOnRules?.associations?.[0] as AssociationRecord[];
    const selectedAddOns = getAddedAddOns(includedAddonCodes, offer as Offer, addOnAssociations);
    const visibilitiesConsideringRing = isRingSub
      ? filterVisibilitiesFromRingSubs(addOnVisibilities)
      : addOnVisibilities;

    const { addOnsToAdd, addOnsToUpdate, addOnsToRemove } = filterChangeOfferAddonsFromRingSubs({
      ...handleChangeOfferResult,
      isRingSub,
    });

    const subscriptionAddOns = Array.from(new Set([...addOnsToAdd, ...addOnsToUpdate]));

    const offerAndFeeMap = prepareOfferAndServiceFeeMap(true, addOnRules, [onlineModel], undefined, subscription);
    const selectedServiceFee = getSelectedServiceFee(offerAndFeeMap, offer?.commercialProducts[0].commercialProductId);

    const addOnSet = Array.from(new Set([...selectedAddOns, ...subscriptionAddOns]));

    const formAddOns =
      addOnSet.filter(addOn =>
        visibilitiesConsideringRing.map(visibility => visibility.addOnGuidCode).includes(addOn.addOnCode)
      ) || [];

    const { codesToAdd, codesToUpdate, codesToRemove } = getAddOnPayload(
      addOnsToAdd,
      addOnsToUpdate,
      addOnsToRemove,
      isRingSub,
      { addedAddOns: formAddOns, removedAddOns: [] }
    );

    const formatToRequest = (addOnCodes: string[]) => {
      return addOnCodes.map(code => ({
        addOnCode: code,
      })) as AddedAddon[];
    };

    return {
      campaignAssociationCode,
      commercialProductCode,
      addOnChanges: {
        addOnsToAdd: formatToRequest(codesToAdd),
        addOnsToUpdate: formatToRequest(codesToUpdate),
        addOnsToRemove: formatToRequest(codesToRemove),
      },
      changeFeePriceItemId: selectedServiceFee?.productId,
    };
  };

  const handleSubmit = async () => {
    if (!subscription || !campaigns?.campaignContexts || !subscriptionHeader) {
      return;
    }

    const subscriptionChangeFeaturesRequest = buildChangeRequest();

    await changeSubscriptionFeatures(
      subscriptionChangeFeaturesRequest,
      subscriptionId,
      subscriptionHeader?.accountMasterId
    );
  };

  return (
    <CampaignModal
      subscription={subscription}
      commercialProduct={commercialProduct}
      campaignAssociation={campaignContext?.campaignAssociations?.[0] as CampaignAssociation}
      onSubmit={handleSubmit}
      mdmId={subscriptionHeader.accountMasterId!}
    />
  );
};
